import type { JobResponseType, JobWithRealisationResponseType } from './types';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';
import { ResolutionDate } from '../telematics/components/Colorizer/ResolutionDate';

export class Job {
    private jobResponse: JobResponseType | JobWithRealisationResponseType | null;
    private _innerId: string;
    // private coords_set_manually: boolean = false;

    constructor() {
        this._innerId = Math.random().toString(36).substring(7);
        this.jobResponse = null;
    }

    public getLabel(): string|null {
        return this.jobResponse?.special_attributes?.label ?? null;
    }

    public editable() {
        if (this.jobResponse === null) {
            return false;
        }
        return this.jobResponse.process_stage < 20;
    }

    public set(jobResponse: JobResponseType): this {
        this.jobResponse = jobResponse;
        return this;
    }

    public getRejectedAssignments(): number[] {
        return this.jobResponse?.rejected_assignments || [];
    }

    public isRejected(): boolean {
        // return this.getRejectedAssignments().length > 0;
        return this.jobResponse?.rejected || false;
    }

    public get innerId(): string {
        return this._innerId;
    }

    public coordsSetManually(): boolean {
        return this.jobResponse?.coords_set_manually || false;
    }

    public setCoords(x: number, y: number, manually: boolean = false): void {
        if (this.jobResponse) {
            if (!this.jobResponse.geom) {
                this.jobResponse.geom = {
                    type: 'Point',
                    coordinates: [0, 0],
                };
            }
            this.jobResponse.geom.coordinates = [x, y];
            this.jobResponse.coords_accepted = true;
            if (manually) {
                this.jobResponse.coords_set_manually = true;
            }
        }
    }

    public get id(): number | null {
        return this.jobResponse?.id || null;
    }

    public getData(): JobResponseType | JobWithRealisationResponseType | null {
        return this.jobResponse;
    }

    public setTargetDate(targetDate: string | null): void {
        if (this.jobResponse) {
            this.jobResponse.target_date = targetDate === '' ? null : targetDate;
        }
    }

    public getTargetDate(): string | null {
        return this.jobResponse?.target_date || null;
    }

    public getStartOfTimeWindow(): string | null {
        return this.jobResponse?.special_attributes?.start_of_time_window || null;
    }

    public setStartOfTimeWindow(startOfTimeWindow: string | null): void {
        this.updateSpecialAttributeValue('start_of_time_window', startOfTimeWindow);
    }

    public getEndOfTimeWindow(): string | null {
        return this.jobResponse?.special_attributes?.end_of_time_window || null;
    }

    public setEndOfTimeWindow(endOfTimeWindow: string | null): void {
        this.updateSpecialAttributeValue('end_of_time_window', endOfTimeWindow);
    }

    public updateSpecialAttributeValue(key: string, value: any): void {
        if (this.jobResponse) {
            if (this.jobResponse.special_attributes === null) {
                this.jobResponse.special_attributes = {
                    [key]: value,
                };
            } else {
                this.jobResponse.special_attributes[key] = value;
            }
        }
    }

    public updateAddressAttributeValue(
        key: string,
        value: any,
        copyAllToClient: boolean = true
    ): void {
        if (this.jobResponse) {
            if (this.jobResponse.address_attributes === null) {
                this.jobResponse.address_attributes = {
                    [key]: value,
                };
            } else {
                this.jobResponse.address_attributes[key] = value;
            }
        }
        if (copyAllToClient) {
            this.copyAddressAttributesToClientAttributes();
        }
    }

    public updateClientAttributeValue(key: string, value: any): void {
        if (this.jobResponse) {
            if (this.jobResponse.client_attributes === null) {
                this.jobResponse.client_attributes = {
                    [key]: value,
                };
            } else {
                this.jobResponse.client_attributes[key] = value;
            }
        }
    }

    public getLatestDeliveryDate(): string | null {
        return this.jobResponse?.special_attributes?.latest_delivery_date || null;
    }

    public getEarliestDeliveryDate(): string | null {
        return this.jobResponse?.special_attributes?.earliest_delivery_date || null;
    }

    public copyAddressAttributesToClientAttributes(): void {
        // const iso8859_1Bytes = new Uint8Array([101, 120, 97, 109, 112, 108, 101, 243, 115, 116, 114, 105, 110, 103]);
        // // Create a TextDecoder for ISO-8859-1
        // const decoder = new TextDecoder('iso-8859-1');

        // Decode the byte array to a string using the specified encoding
        // const utf8String = decoder.decode(iso8859_1Bytes);

        if (this.jobResponse && this.jobResponse.address_attributes !== null) {
            // this.jobResponse.client_attributes = {...this.jobResponse.address_attributes};
            // this.jobResponse.client_attributes = {};
            // Object.keys(this.jobResponse.address_attributes).forEach((key) => {
            //     try {
            //         this.jobResponse.client_attributes[key] = this.jobResponse.address_attributes[key].replace(/\xf3/g, 'ó') ?? '';
            //     } catch (e) {
            //         console.warn('copyAddressAttributesToClientAttributes',key, e);
            //         this.jobResponse.client_attributes[key] = '';
            //     }
            // });
            this.jobResponse.target_date = this.getTargetDate();

            // this.jobResponse.special_attributes = {};
            // this.jobResponse.special_attributes['delivery_or_collection'] = this.jobResponse?.special_attributes?.delivery_or_collection ?? '';
            // this.jobResponse.special_attributes['start_of_time_window'] = this.getStartOfTimeWindow() || '';
            // this.jobResponse.special_attributes['end_of_time_window'] = this.getEndOfTimeWindow() || '';
            this.jobResponse.special_attributes['latest_delivery_date'] =
                this.getLatestDeliveryDate() === null
                    ? ''
                    : ResolutionDate.getShortFormat(new Date(this.getLatestDeliveryDate() || ''));
            this.jobResponse.special_attributes['earliest_delivery_date'] =
                this.getEarliestDeliveryDate() === null
                    ? ''
                    : ResolutionDate.getShortFormat(new Date(this.getEarliestDeliveryDate() || ''));
            // this.jobResponse.special_attributes['parcels'] = String(this.jobResponse?.special_attributes?.parcels || '1');
            // this.jobResponse.special_attributes['total_weight'] = String(this.jobResponse?.special_attributes?.total_weight || '0');
            // this.jobResponse.special_attributes['fill_percentage'] = String(this.jobResponse?.special_attributes?.fill_percentage || '0');
            // this.jobResponse.special_attributes['external_id'] = this.jobResponse?.special_attributes?.external_id || '';

            // Object.keys(this.jobResponse.special_attributes).forEach((key) => {
            //     try {
            //         this.jobResponse.client_attributes[key] = this.jobResponse.special_attributes[key].replace(/\xf3/g, 'ó') ?? '';
            //     } catch (e) {
            //         console.warn('copyAddressAttributesToClientAttributes', key, e);
            //         this.jobResponse.client_attributes[key] = '';
            //     }
            // });
        }
    }

    public getParcelCount(): number | null {
        return this.jobResponse?.special_attributes?.parcels || null;
    }

    public getTotalWeight(): number | null {
        return this.jobResponse?.special_attributes?.total_weight || null;
    }

    public getCity(): string {
        return this.jobResponse?.address_attributes?.city || '';
    }

    public getGeocodedCity(): string {
        return this.jobResponse?.computed_address?.city || '';
    }

    public getGeocodedStreet(): string {
        return this.jobResponse?.computed_address?.street || '';
    }

    public getGeocodedHouse(): string {
        return this.jobResponse?.computed_address?.house || '';
    }

    public getStreet(): string {
        return this.jobResponse?.address_attributes?.street || '';
    }

    public getHouse(): string {
        return this.jobResponse?.address_attributes?.house || '';
    }

    public getX(): number | null {
        let n = this.jobResponse?.geom?.coordinates[0] || null;
        if (n === null) {
            return null;
        }
        return parseFloat(n.toFixed(5));
    }

    public getY(): number | null {
        let n = this.jobResponse?.geom?.coordinates[1] || null;
        if (n === null) {
            return null;
        }
        return parseFloat(n.toFixed(5));
    }
}
